// @ts-strict-ignore
import type { Moment } from 'moment';
import moment from 'moment';
import momentTz from 'moment-timezone';
import pick from 'lodash/pick';

import type { WorkflowScheduleConfig } from './types';
import {
  WorkflowScheduleIntervals,
  WorkflowScheduleDates,
  WorkflowScheduleWeekdays,
} from './types';
import { WORKFLOW_TYPES } from '~/constants';
import { deleteOptionalUnappliedMlaFilters } from './flowHelper';
import generateToken from '~/dashboard/views/settings/sandbox-profiles/helpers';
import apiCalls from '~/helpers/apiCalls';
import { OUTPUT_ATTRIBUTE_DEFAULT_EXPRESSION_BY_DATA_TYPE } from './constants';
import { saveMatrixModels } from './matrix-models/services';

export const MOMENT_WEEKDAY_DAY_TO_NUM = {
  [WorkflowScheduleWeekdays.SUN]: 0,
  [WorkflowScheduleWeekdays.MON]: 1,
  [WorkflowScheduleWeekdays.TUE]: 2,
  [WorkflowScheduleWeekdays.WED]: 3,
  [WorkflowScheduleWeekdays.THU]: 4,
  [WorkflowScheduleWeekdays.FRI]: 5,
  [WorkflowScheduleWeekdays.SAT]: 6,
};

export const MOMENT_WEEKDAY_NUM_TO_DAY = {
  0: WorkflowScheduleWeekdays.SUN,
  1: WorkflowScheduleWeekdays.MON,
  2: WorkflowScheduleWeekdays.TUE,
  3: WorkflowScheduleWeekdays.WED,
  4: WorkflowScheduleWeekdays.THU,
  5: WorkflowScheduleWeekdays.FRI,
  6: WorkflowScheduleWeekdays.SAT,
};

const SHORT_TO_LONG_WEEKDAY = {
  [WorkflowScheduleWeekdays.SUN]: 'Sundays',
  [WorkflowScheduleWeekdays.MON]: 'Mondays',
  [WorkflowScheduleWeekdays.TUE]: 'Tuesdays',
  [WorkflowScheduleWeekdays.WED]: 'Wednesdays',
  [WorkflowScheduleWeekdays.THU]: 'Thursdays',
  [WorkflowScheduleWeekdays.FRI]: 'Fridays',
  [WorkflowScheduleWeekdays.SAT]: 'Saturdays',
};

export const scheduleRunCalculator = {
  getNextRunDaily(currentTime: Moment, frequencyConfig: WorkflowScheduleConfig): Moment {
    const {
      time: frequencyTime,
    } = frequencyConfig;

    const [frequencyHour, frequencyMinute] = frequencyTime.split(':');

    const nextRunTime = moment().hour(Number(frequencyHour))
      .minute(Number(frequencyMinute))
      .seconds(0)
      .millisecond(0);

    if (currentTime > nextRunTime) {
      nextRunTime.add(1, 'days');
    }

    return nextRunTime;
  },

  getNextRunWeekly(currentTime: Moment, frequencyConfig: WorkflowScheduleConfig): Moment {
    const {
      time: frequencyTime,
      weekDay: frequencyWeekDay,
    } = frequencyConfig;

    const [frequencyHour, frequencyMinute] = frequencyTime.split(':');

    const runTimes = frequencyWeekDay.split(',').map(day => {
      const m = moment().day(MOMENT_WEEKDAY_DAY_TO_NUM[day])
        .hour(Number(frequencyHour))
        .minute(Number(frequencyMinute))
        .seconds(0)
        .millisecond(0);
      if (currentTime > m) {
        m.add(7, 'days');
      }
      return m;
    });
    const [nextRunTime] = runTimes.sort((a, b) => Number(a) - Number(b));
    return nextRunTime;
  },

  getNextRunMonthly(currentTime: Moment, frequencyConfig: WorkflowScheduleConfig): Moment {
    const {
      time: frequencyTime,
      date: frequencyDate,
    } = frequencyConfig;

    const [frequencyHour, frequencyMinute] = frequencyTime.split(':');

    const dayOfMonth = frequencyDate === WorkflowScheduleDates.LAST
      ? (moment().endOf('month').date())
      : Number(frequencyDate);

    const nextRunTime = moment().date(dayOfMonth)
      .hour(Number(frequencyHour))
      .minute(Number(frequencyMinute))
      .seconds(0)
      .millisecond(0);

    if (currentTime > nextRunTime) {
      nextRunTime.add(1, 'months');
      if (frequencyDate === WorkflowScheduleDates.LAST) {
        const lastDayOfMonth = moment(nextRunTime).endOf('month').date();
        nextRunTime.date(lastDayOfMonth);
      }
    }

    return nextRunTime;
  },

  getNextRunAnnually(currentTime: Moment, frequencyConfig: WorkflowScheduleConfig): Moment {
    const {
      time: frequencyTime,
      month: frequencyMonth,
      day: frequencyDay,
      startYear: frequencyStartYear,
    } = frequencyConfig;

    const [frequencyHour, frequencyMinute] = frequencyTime.split(':');

    const nextRunTime = moment().year(frequencyStartYear)
      .month(frequencyMonth - 1)
      .date(frequencyDay)
      .hour(Number(frequencyHour))
      .minute(Number(frequencyMinute))
      .seconds(0)
      .millisecond(0);

    while (currentTime > nextRunTime) {
      nextRunTime.year(frequencyStartYear + 1);
    }

    return nextRunTime;
  },

  getNextRun(
    frequencyInterval: `${WorkflowScheduleIntervals}`,
    frequencyConfig: WorkflowScheduleConfig,
  ): Moment | null {
    const currentTimeEastern = momentTz().tz('America/New_York');

    if (frequencyInterval === WorkflowScheduleIntervals.DAILY) {
      return scheduleRunCalculator.getNextRunDaily(currentTimeEastern, frequencyConfig);
    }

    if (frequencyInterval === WorkflowScheduleIntervals.WEEKLY) {
      return scheduleRunCalculator.getNextRunWeekly(currentTimeEastern, frequencyConfig);
    }

    if (frequencyInterval === WorkflowScheduleIntervals.MONTHLY) {
      return scheduleRunCalculator.getNextRunMonthly(currentTimeEastern, frequencyConfig);
    }

    if (frequencyInterval === WorkflowScheduleIntervals.ANNUALLY) {
      return scheduleRunCalculator.getNextRunAnnually(currentTimeEastern, frequencyConfig);
    }

    return null;
  },
};
export const getTzConvertedMomentObj = ({
  timeString,
  daysString,
  fromTimeZone,
  toTimeZone,
}: {
  timeString: string;
  daysString: string;
  fromTimeZone: string;
  toTimeZone: string;
}): {
  formattedTime: string;
  adjustedDays: string;
  convertedMomentObj: Moment;
} => {
  const timeMoment = moment().tz(fromTimeZone).startOf('day');
  const [hour, minute] = timeString.split(':');
  const convertedMomentDays = daysString.split(',').map(day => {
    const momentDayNum = MOMENT_WEEKDAY_DAY_TO_NUM[day];
    const timeClone = timeMoment.clone();
    timeClone.day(momentDayNum).hour(Number(hour)).minute(Number(minute));
    return timeClone.tz(toTimeZone);
  });
  return {
    // Can take arbitrary day from day array to get the correct time
    formattedTime: convertedMomentDays[0].format('HH:mm'),
    adjustedDays: convertedMomentDays
      .map(momDay => MOMENT_WEEKDAY_NUM_TO_DAY[momDay.day()])
      // sort again in case we have wrapped around the week due to tz conversion
      .sort((day1, day2) => MOMENT_WEEKDAY_DAY_TO_NUM[day1] - MOMENT_WEEKDAY_DAY_TO_NUM[day2])
      .join(','),
    convertedMomentObj: convertedMomentDays[0],
  };
};

// Convert between Eastern and UTC time
export const getAdjustedDateConfig = (
  timeDayObj: { timeString: string; daysString: string; asOfClockTime?: string },
  outputTimeZone: 'Eastern' | 'UTC',
) => {
  const { timeString, daysString, asOfClockTime } = timeDayObj;

  let fromTimeZone = 'UTC';
  let toTimeZone = 'America/New_York';
  if (outputTimeZone === 'UTC') {
    fromTimeZone = 'America/New_York';
    toTimeZone = 'UTC';
  }
  const {
    formattedTime,
    adjustedDays,
  } = getTzConvertedMomentObj({
    timeString,
    daysString,
    fromTimeZone,
    toTimeZone,
  });

  let asOfClockTimeFormatted: string = null;
  if (asOfClockTime) {
    ({ formattedTime: asOfClockTimeFormatted } = getTzConvertedMomentObj({
      timeString: asOfClockTime,
      daysString,
      fromTimeZone,
      toTimeZone,
    }));
  }
  return {
    time: formattedTime,
    weekDay: adjustedDays,
    asOfClockTimeFormatted,
  };
};

export const getUpdatedWeekDay = (clickedWeekday: string, configWeekdays = '') => {
  let daysArr: string[];
  if (configWeekdays.includes(clickedWeekday)) {
    daysArr = configWeekdays.split(',');
    const weekDayIdx = daysArr.indexOf(clickedWeekday);
    daysArr.splice(weekDayIdx, 1);
  } else {
    daysArr = configWeekdays ? configWeekdays.split(',') : [];
    daysArr.push(clickedWeekday);
  }
  return daysArr.sort((day1, day2) => MOMENT_WEEKDAY_DAY_TO_NUM[day1] - MOMENT_WEEKDAY_DAY_TO_NUM[day2]).join(',');
};

export const getWeekdaysLabel = (weekdays: string) => {
  if (!weekdays) {
    return '';
  }
  const weekdaysArr = weekdays.split(',');
  if (weekdaysArr.length > 5) {
    return weekdaysArr.map(day => day[0]).join('/');
  }
  if (weekdaysArr.length > 2) {
    return weekdaysArr.map(day => day[0] + day.slice(1).toLowerCase()).join('/');
  }
  return weekdaysArr.map(day => SHORT_TO_LONG_WEEKDAY[day]).join(', ');
};

/*
  Temporarily hiding features for Portfolio Evaluations
  Epic ID: 109173
*/
const attrsAllowedAccountHistory = [
  'Account Balance Multi-Level Attribute',
  'Account Status Multi-Level Attribute',
  'PII Changes Multi-Level Attribute',
  'Max Account Days On Books',
  'Total Number Of Primary Accounts',
  'Total Number Of Secondary Accounts',
];

export const isAttributeAllowedInPortfolioWorkflow = (services, serviceId, attributeName) => {
  const attrsDisallowedTransactionHistory = [
    // 'Counterparty Multi-Level Attribute',
  ];

  if (services[serviceId]?.name === 'Account History') {
    return attrsAllowedAccountHistory.includes(attributeName);
  }

  if (services[serviceId]?.name === 'Transaction History') {
    return !attrsDisallowedTransactionHistory.includes(attributeName);
  }

  return true;
};

export const getAllowedAttributesForPortfolioWorkflow = (
  workflowType, actionName, allActiveAttributes,
) => {
  if (workflowType === WORKFLOW_TYPES.PORTFOLIO && actionName === 'Account History') {
    return Object.keys(allActiveAttributes)
      .filter(attrKey => attrsAllowedAccountHistory.includes(allActiveAttributes[attrKey]?.name))
      .reduce((obj, key) => ({ ...obj, [key]: allActiveAttributes[key] }), {});
  }
  return allActiveAttributes;
};

export default {
  scheduleRunCalculator,
  isAttributeAllowedInPortfolioWorkflow,
  getAllowedAttributesForPortfolioWorkflow,
};

const cloneMatrixModels = ({
  customerId,
  matrixModels,
  typhonDomain,
}) => matrixModels.length && saveMatrixModels(
  typhonDomain,
  matrixModels.map(matrixModel => ({
    ...pick(matrixModel, [
      'actionId',
      'matrix',
      'matrix_data_type',
      'name',
      'x_axis_header',
      'x_axis_header_data_type',
      'y_axis_header',
      'y_axis_header_data_type',
    ]),
    customerId,
  })),
);

export const cloneWorkflow = async (
  typhonDomain,
  workflow,
  sourceCustomerId,
  returnResponse = false,
  useHydratedPAActions = false,
) => {
  const {
    processedThresholds,
    processedDataRoutes,
  } = deleteOptionalUnappliedMlaFilters({
    thresholds: workflow.thresholds,
    dataRoutes: workflow.dataRoutes,
  });

  const formattedSandboxPersonas = workflow.sandboxPersonas?.map(persona => (
    {
      ...persona,
      sandbox_persona_id: undefined,
      sandbox_persona_token: generateToken(20),
      values: persona.values?.map(({ attribute_id, value }) => (
        {
          attribute_id,
          value,
        }
      )),
    }
  ));

  const formatWorkflowActionOptions = (usePAHydration = true) => {
    if (usePAHydration && workflow.actionOptions) {
      return workflow.actionOptions.filter(
        ({ key }) => (
          key !== 'matrix_model_token' && key !== 'matrix_model_public_version'
        ),
      );
    }

    if (!usePAHydration && workflow.actionOptionsWithoutPAHydration) {
      return workflow.actionOptionsWithoutPAHydration.filter(
        ({ key }) => (
          key !== 'matrix_model_token' && key !== 'matrix_model_public_version'
        ),
      );
    }

    return [];
  };

  return apiCalls.workflows.clone({
    hasSilentAlertMessage: true,
    returnResponse,
    workflow: {
      ...workflow,
      thresholds: processedThresholds,
      dataRoutes: processedDataRoutes,
      actionOptions: [
        ...formatWorkflowActionOptions(useHydratedPAActions),
        ...(await cloneMatrixModels({
          typhonDomain,
          matrixModels: workflow.matrixModels,
          customerId: workflow.customerId,
        }) || {}).matrixModelActionOptions || [],
      ],
      sandboxPersonas: formattedSandboxPersonas || undefined,
      sourceCustomerId,
    },
  });
};

export const buildActionFromPublishedAttribute = (publishedAttribute, isOutputAttribute) => {
  const {
    attributeName: publishedAttributeName,
    attributeToken: publishedAttributeToken,
    dataType,
    jqFilter: inputAttrJqFilter,
  } = publishedAttribute;

  const options = [
    { key: 'name', value: publishedAttributeName, publishedAttributeToken },
    { key: 'data_type', value: dataType, publishedAttributeToken },
    {
      key: 'use_jq',
      value: isOutputAttribute ? 'false' : `${!!inputAttrJqFilter}`,
      publishedAttributeToken,
    },
    {
      key: 'jq_function',
      value: isOutputAttribute ? '' : (inputAttrJqFilter || ''),
      publishedAttributeToken,
    },
  ];

  if (isOutputAttribute) {
    // Spread the default value so that editing the jsonValue does not edit the reference
    const jsonValue = [...OUTPUT_ATTRIBUTE_DEFAULT_EXPRESSION_BY_DATA_TYPE[dataType]];
    const expressionOption = {
      key: 'expression',
      value: JSON.stringify(jsonValue),
      jsonValue,
      publishedAttributeToken,
    };

    options.push(expressionOption);
  }

  return {
    type: isOutputAttribute ? 'output' : 'input',
    name: publishedAttributeName,
    publishedAttributeToken,
    options,
  };
};
